import { useState } from 'react';
import { useFiltersStore } from '../stores/filtersStore';
import { searchCustomers } from '../lib/api';
import { useApi } from '../hooks/useApi';
import CustomerList from './CustomerList';
import type { SearchFilters } from '../types';

export default function CustomerSearch() {
  const { filters, setFilters, clearFilters, setSearchResults, searchResults } = useFiltersStore();
  const [localFilters, setLocalFilters] = useState<SearchFilters>(filters);
  const { loading, error, execute } = useApi(searchCustomers);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(localFilters);
    const result = await execute(localFilters);
    if (result) {
      setSearchResults(result.customers);
    }
  };

  const handleClear = () => {
    setLocalFilters({});
    clearFilters();
  };

  const hasFilters = Object.values(localFilters).some((v) => v);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Customer Search</h1>
        <p className="mt-1 text-sm text-gray-500">
          Search for customers by email, name, or external ID.
        </p>
      </div>

      <form onSubmit={handleSearch} className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label htmlFor="email" className="label">
              Email
            </label>
            <input
              type="text"
              id="email"
              className="input"
              placeholder="customer@example.com"
              value={localFilters.email || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, email: e.target.value })}
            />
          </div>

          <div>
            <label htmlFor="firstName" className="label">
              First Name
            </label>
            <input
              type="text"
              id="firstName"
              className="input"
              placeholder="John"
              value={localFilters.firstName || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, firstName: e.target.value })}
            />
          </div>

          <div>
            <label htmlFor="lastName" className="label">
              Last Name
            </label>
            <input
              type="text"
              id="lastName"
              className="input"
              placeholder="Doe"
              value={localFilters.lastName || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, lastName: e.target.value })}
            />
          </div>

          <div>
            <label htmlFor="externalId" className="label">
              External ID
            </label>
            <input
              type="text"
              id="externalId"
              className="input"
              placeholder="EXT-12345"
              value={localFilters.externalId || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, externalId: e.target.value })}
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClear}
            className="btn-secondary"
            disabled={!hasFilters}
          >
            Clear
          </button>
          <button type="submit" className="btn-primary" disabled={loading || !hasFilters}>
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </form>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {searchResults.length > 0 && <CustomerList customers={searchResults} />}

      {!loading && !error && searchResults.length === 0 && hasFilters && (
        <div className="text-center py-12">
          <p className="text-gray-500">No customers found matching your search criteria.</p>
        </div>
      )}
    </div>
  );
}
